/**
 * Realtime for team boards AND team documents: joins a `team:{teamId}` phoenix
 * channel per team and feeds its `sync_delta` frames into the Rust engine
 * under the `team:<teamId>` scope — the delivery-only pattern team workspaces
 * use (see hooks/useWorkspaceRealtime mode 1). One registered engine task per
 * team (teamBoardsSync) covers every extension_documents row in that scope,
 * task-manager and documents alike, so no separate registration is needed
 * here. The engine applies the delta and emits sync:remote-applied, which
 * useSyncEngine turns into bumpBoards()/bumpDocumentsTree() so the Task
 * Manager / Documents tree reloads from disk — this stays as the
 * authoritative fallback (board/tree-level edits, deletes, and correcting any
 * WS/engine divergence).
 *
 * WS-first fast path: the same frame's records are also published straight to
 * any open TaskBoardTab (publishBoardChanges) or Documents view
 * (publishDocumentChanges) — split by owning extension_id (see
 * splitExtensionDocumentChanges) — so group/task/document edits paint
 * immediately instead of waiting on the engine write + disk re-read round
 * trip. Persistence (sync_apply_broadcast) still runs regardless — this is a
 * read-side-only optimization, not a bypass.
 *
 * Reconnects gap-fill with a team pull. Mount once (App.js), next to
 * useSyncEngine. No-op outside the desktop app.
 */
import { useEffect, useRef } from 'react';
import { invoke } from '@tauri-apps/api/core';
import { Presence } from 'phoenix';
import { isTauri } from '../utils/tauri';
import { useAuthStore } from '../store/authStore';
import { useTeamsStore } from '../store/teamsStore';
import { teamScopeId, remoteChangeToRecord } from '../extensions/bundled/task-manager/boardsStore';
import { publishBoardChanges } from '../extensions/bundled/task-manager/boardsUiStore';
import { remoteChangeToDocRecord } from '../extensions/bundled/documents/documentsStore';
import { publishDocumentChanges, bumpDocumentsTree } from '../extensions/bundled/documents/documentsUiStore';
import { useTeamPresenceStore } from '../store/teamPresenceStore';
import {
  joinTeamChannel,
  leaveTeamChannel,
  leaveAllTeamChannels,
  addTeamEventListener,
  TEAM_REJOINED_EVENT,
} from '../services/teamChannelService';
import { ensureTeamBoardsSync } from '../services/teamBoardsSync';

/** A block record of a document body, or the delete of one (deletes carry no data — sync.ex). */
const isBlockChange = (r) => r.record_type === 'docblock' || (r.deleted && typeof r.id === 'string' && r.id.includes('::b:'));

const invokeSync = async (cmd, args) => {
  try {
    return await invoke(cmd, args);
  } catch (err) {
    console.warn(`[team-boards] ${cmd} failed:`, err);
    return null;
  }
};

/**
 * Pure: split a sync_delta frame's `extension_documents` changes by the
 * extension that owns them, so each fast path only ever sees its own records.
 * Fixes a latent bug where every extension_documents change (task-manager AND
 * documents) flowed into the boards publish unfiltered.
 *
 * Upserts carry `data.extension_id` (see decomposeBoard / documentsStore's
 * record shapes), so those route cleanly. Deletes do not — the server nils
 * out `data` entirely for a delete (change_entry/format_entity in
 * backend_pheonix/sync.ex), so a delete can't be attributed to an extension
 * here and lands in neither bucket; it's still applied correctly (just not on
 * this WS-fast-path) via the caller's unfiltered sync_apply_broadcast below,
 * followed by the disk-reconcile bump.
 */
export function splitExtensionDocumentChanges(changes) {
  const extChanges = (changes || []).filter((c) => c?.entity_type === 'extension_documents');
  return {
    boardChanges: extChanges.filter((c) => c?.data?.extension_id === 'task-manager'),
    docChanges: extChanges.filter((c) => c?.data?.extension_id === 'documents'),
  };
}

export function useTeamBoardsRealtime() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const teams = useTeamsStore((s) => s.teams);
  const joinedRef = useRef(new Set());
  // Per-team accumulated phoenix Presence state, so presence_diff frames (which
  // are deltas) can be folded onto the right team's prior state.
  const presenceStatesRef = useRef({});

  // Delivery-only subscriptions (mounted once): forward every team's sync_delta
  // to the engine, and gap-fill on rejoin. Scope comes from the frame's teamId.
  useEffect(() => {
    if (!isTauri()) return undefined;
    const unsubs = [
      addTeamEventListener('sync_delta', (payload, { teamId }) => {
        const changes = payload?.changes || [];
        const scopeKey = teamScopeId(teamId);
        // A bulk server change (a board shared with the team) arrives as ONE
        // frame with no entries, only the new max_seq: pull once.
        if (!changes.length && payload?.max_seq != null) {
          invokeSync('sync_pull_now', { workspaceId: scopeKey });
          return;
        }
        // Fast path first (synchronous, no IPC): paint group/task edits into
        // an open board, and folder/document/docbody edits into an open
        // Documents view, immediately — split by owning extension so one
        // extension's changes never reach the other's publish (see
        // splitExtensionDocumentChanges).
        const { boardChanges, docChanges } = splitExtensionDocumentChanges(changes);
        if (boardChanges.length) publishBoardChanges(scopeKey, boardChanges.map(remoteChangeToRecord));
        if (docChanges.length) {
          const records = docChanges.map(remoteChangeToDocRecord);
          publishDocumentChanges(scopeKey, records);
          // A body saved per block (docBlocks.js) changes nothing in the tree:
          // a batch of only those must not reload the sidebar on every save.
          if (records.some((r) => !isBlockChange(r))) bumpDocumentsTree();
        }
        // Persistence still runs regardless, same as before — unfiltered, so
        // it stays the source of truth for every change, including deletes.
        invokeSync('sync_apply_broadcast', {
          workspaceId: scopeKey,
          changes,
        });
      }),
      addTeamEventListener(TEAM_REJOINED_EVENT, (_payload, { teamId }) => {
        invokeSync('sync_pull_now', { workspaceId: teamScopeId(teamId) });
      }),
      addTeamEventListener('presence_state', (payload, { teamId }) => {
        const next = Presence.syncState(presenceStatesRef.current[teamId] || {}, payload || {});
        presenceStatesRef.current[teamId] = next;
        useTeamPresenceStore.getState().setTeamPresence(teamId, next);
      }),
      addTeamEventListener('presence_diff', (payload, { teamId }) => {
        const next = Presence.syncDiff(presenceStatesRef.current[teamId] || {}, payload || {});
        presenceStatesRef.current[teamId] = next;
        useTeamPresenceStore.getState().setTeamPresence(teamId, next);
      }),
    ];
    return () => unsubs.forEach((unsub) => unsub());
  }, []);

  // Join/leave team channels as team membership settles.
  useEffect(() => {
    if (!isTauri()) return undefined;
    if (!isAuthenticated) {
      leaveAllTeamChannels();
      joinedRef.current.forEach((id) => useTeamPresenceStore.getState().clearTeamPresence(id));
      joinedRef.current = new Set();
      presenceStatesRef.current = {};
      return undefined;
    }
    const ids = new Set((teams || []).filter((t) => t?.id != null).map((t) => String(t.id)));
    // Leave channels for teams the user is no longer in.
    joinedRef.current.forEach((id) => {
      if (!ids.has(id)) {
        leaveTeamChannel(id);
        useTeamPresenceStore.getState().clearTeamPresence(id);
        delete presenceStatesRef.current[id];
      }
    });
    // Register the engine scopes BEFORE joining the channels: a sync_delta
    // frame that lands before sync_register_team completes hits an engine with
    // no task to drain its needs_pull flag, so a gap frame right after
    // launch/login is silently dropped until the first registered pull cycle.
    // ensureTeamBoardsSync is idempotent, so racing useSyncEngine's own call
    // is harmless.
    let cancelled = false;
    (async () => {
      await ensureTeamBoardsSync(teams);
      if (!cancelled) ids.forEach((id) => joinTeamChannel(id));
    })();
    joinedRef.current = ids;
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, teams]);
}
